<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/lab')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-flask-empty-outline me-2"></i>Lab Results Entry</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-amber" @click="autoRefresh = !autoRefresh">
            <i class="mdi" :class="autoRefresh ? 'mdi-pause' : 'mdi-play'"></i>
            {{ autoRefresh ? 'Pause' : 'Auto-refresh' }}
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterStatus=''" :class="{ selected: !filterStatus }">
          <div class="hms-stat-num">{{ requests.length }}</div>
          <div class="hms-stat-lbl">Open Requests</div>
        </div>
        <div class="hms-stat" @click="filterStatus='pending'" :class="{ selected: filterStatus==='pending' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.pending_requests || 0 }}</div>
          <div class="hms-stat-lbl">Pending Collection</div>
        </div>
        <div class="hms-stat" @click="filterStatus='sample_collected'" :class="{ selected: filterStatus==='sample_collected' }">
          <div class="hms-stat-num hms-stat-info">{{ stats.collected_requests || 0 }}</div>
          <div class="hms-stat-lbl">Collected</div>
        </div>
        <div class="hms-stat" @click="filterStatus='processing'" :class="{ selected: filterStatus==='processing' }">
          <div class="hms-stat-num hms-stat-purple">{{ stats.processing_requests || 0 }}</div>
          <div class="hms-stat-lbl">Processing</div>
        </div>
        <div class="hms-stat" @click="filterPriority='stat'; filterStatus=''" :class="{ selected: filterPriority==='stat' }">
          <div class="hms-stat-num hms-stat-danger">{{ stats.urgent_open || 0 }}</div>
          <div class="hms-stat-lbl">STAT / Urgent</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-success">{{ stats.completed_today || 0 }}</div>
          <div class="hms-stat-lbl">Completed Today</div>
        </div>
      </div>

      <div v-if="loading && !requests.length" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="row g-3">
        <!-- LEFT — Worklist -->
        <div class="col-lg-4">
          <div class="hms-card">
            <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <div class="hms-search-wrap">
                <i class="mdi mdi-magnify"></i>
                <input class="hms-search" v-model="search" placeholder="Search request #, patient..." />
              </div>
              <select class="hms-input hms-filter" v-model="filterPriority">
                <option value="">All</option>
                <option value="stat">STAT</option>
                <option value="urgent">Urgent</option>
                <option value="routine">Routine</option>
              </select>
            </div>

            <div class="hms-section-label">WORKLIST ({{ filteredRequests.length }})</div>
            <div v-if="!filteredRequests.length" class="hms-empty-mini">
              <i class="mdi mdi-checkbox-marked-circle-outline"></i>Worklist empty.
            </div>
            <div v-else class="hms-q-list">
              <div v-for="r in filteredRequests" :key="r.request_id"
                   class="hms-q-item" :class="['pri-' + r.priority, { active: active && active.Request && active.Request.request_id === r.request_id }]"
                   @click="selectRequest(r.request_id)">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <span class="hms-mono">{{ r.request_number }}</span>
                  <span class="hms-badge" :class="priorityBadge(r.priority)">{{ r.priority }}</span>
                  <span class="hms-badge hms-badge-info">{{ r.status }}</span>
                  <span class="hms-q-time ms-auto">{{ formatMins(r.minutes_open) }}</span>
                </div>
                <div class="hms-q-name">{{ r.first_name }} {{ r.last_name }}</div>
                <div class="hms-muted-sm">{{ r.patient_no }} · {{ ageStr(r.dob) }} · {{ r.gender }}</div>
                <div class="hms-progress-strip">
                  <span class="hms-pip-num" v-if="r.pending_items > 0"    title="Pending"   ><i class="mdi mdi-test-tube-empty"></i> {{ r.pending_items }}</span>
                  <span class="hms-pip-num" v-if="r.collected_items > 0"  title="Collected" ><i class="mdi mdi-test-tube"></i> {{ r.collected_items }}</span>
                  <span class="hms-pip-num" v-if="r.processing_items > 0" title="Processing"><i class="mdi mdi-cog-clockwise"></i> {{ r.processing_items }}</span>
                  <span class="hms-pip-num" v-if="r.resulted_items > 0"   title="Resulted"  ><i class="mdi mdi-check"></i> {{ r.resulted_items }}</span>
                  <span class="hms-pip-num verified" v-if="r.verified_items > 0" title="Verified"><i class="mdi mdi-check-decagram"></i> {{ r.verified_items }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT — Active request -->
        <div class="col-lg-8">
          <div v-if="!active" class="hms-card hms-empty">
            <i class="mdi mdi-flask-outline"></i>Select a request from the worklist.
          </div>

          <div v-else>
            <UnpaidBanner :patient-id="active.Request.patient_id" />
            <!-- Header -->
            <div class="hms-card mb-3">
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <div class="hms-avatar-lg">{{ initials(active.Request) }}</div>
                <div class="flex-fill">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <span class="hms-h">{{ active.Request.patient_name }}</span>
                    <span class="hms-mono hms-muted-sm">{{ active.Request.patient_no }}</span>
                    <span class="hms-badge" :class="priorityBadge(active.Request.priority)">{{ active.Request.priority }}</span>
                    <span class="hms-badge hms-badge-info">{{ active.Request.status }}</span>
                  </div>
                  <div class="hms-muted-sm mt-1">
                    {{ ageStr(active.Request.dob) }} · {{ active.Request.gender }}
                    · Request <span class="hms-mono">{{ active.Request.request_number }}</span>
                    · {{ formatDateTime(active.Request.request_date) }}
                  </div>
                  <div class="hms-muted-sm mt-1" v-if="active.Request.requested_by_name">
                    <i class="mdi mdi-doctor"></i> Requested by {{ active.Request.requested_by_name }}
                  </div>
                  <div v-if="active.Request.clinical_notes" class="hms-info-strip mt-2">
                    <i class="mdi mdi-information-outline"></i> {{ active.Request.clinical_notes }}
                  </div>
                </div>
                <div class="d-flex gap-2 flex-wrap">
                  <button class="hms-btn hms-btn-blue" @click="$router.push('/patients/' + active.Request.patient_id + '/chart')">
                    <i class="mdi mdi-folder-account-outline me-1"></i>Patient Chart
                  </button>
                  <button class="hms-btn hms-btn-blue" @click="printLabReport" :disabled="!canPrint" title="Print results report">
                    <i class="mdi mdi-printer me-1"></i>Print Report
                  </button>
                  <button class="hms-btn hms-btn-blue" @click="aiInterpret" :disabled="aiLoading || !canAiInterpret">
                    <i class="mdi mdi-robot-outline me-1"></i>{{ aiLoading ? 'Thinking...' : 'AI Interpret' }}
                  </button>
                  <SaveAndQueue ref="sqLab" from-point="lab" :saving="processing"
                                save-label="Finalise &amp; Route Patient" saving-label="Routing..."
                                btn-class="hms-btn hms-btn-success" icon="mdi-arrow-right-bold" @save="finaliseLab" />
                </div>
              </div>

              <div v-if="aiResult" class="hms-ai-box mt-3">
                <div class="d-flex align-items-center mb-1">
                  <i class="mdi mdi-robot-outline me-1"></i><strong>AI Interpretation</strong>
                  <button class="hms-btn-icon ms-auto" @click="aiResult = null"><i class="mdi mdi-close"></i></button>
                </div>
                <div class="hms-ai-body">{{ aiResult }}</div>
                <div class="hms-muted-sm mt-1"><i class="mdi mdi-shield-alert-outline"></i> Verify all suggestions clinically before acting.</div>
              </div>
            </div>

            <!-- Items -->
            <div class="hms-card">
              <div class="hms-section-label d-flex align-items-center justify-content-between">
                <span>TESTS ({{ active.Items.length }})</span>
                <div class="d-flex gap-1">
                  <button class="hms-btn hms-btn-sm hms-btn-amber" @click="bulkCollect" :disabled="processing || !canBulkCollect">
                    <i class="mdi mdi-test-tube me-1"></i>Collect All
                  </button>
                </div>
              </div>

              <div v-for="item in active.Items" :key="item.item_id" class="hms-item-card" :class="'st-' + item.status">
                <div class="hms-item-head">
                  <span class="hms-item-name">{{ item.test_name }}</span>
                  <span class="hms-muted-sm">{{ item.sample_type || '—' }}</span>
                  <span class="hms-badge ms-auto" :class="itemStatusBadge(item.status)">{{ item.status }}</span>
                </div>

                <!-- Pending: collect sample -->
                <div v-if="item.status === 'pending'" class="hms-item-body">
                  <div class="hms-muted-sm">Sample not yet collected.</div>
                  <div class="d-flex gap-2 mt-2">
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="collectItem(item)" :disabled="processing">
                      <i class="mdi mdi-test-tube me-1"></i>Mark Collected
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-danger" @click="cancelItem(item)" :disabled="processing">
                      <i class="mdi mdi-close-circle me-1"></i>Cancel
                    </button>
                  </div>
                </div>

                <!-- Sample collected: enter result form OR start processing -->
                <div v-else-if="item.status === 'sample_collected' || item.status === 'processing'" class="hms-item-body">
                  <div class="hms-muted-sm" v-if="item.collected_by_name">
                    <i class="mdi mdi-account-outline"></i> Collected by {{ item.collected_by_name }} · {{ formatDateTime(item.sample_collected_at) }}
                  </div>
                  <div v-if="item.status === 'sample_collected'" class="d-flex gap-2 mt-2 mb-2">
                    <button class="hms-btn hms-btn-sm hms-btn-amber" @click="startProcessing(item)" :disabled="processing">
                      <i class="mdi mdi-cog-clockwise me-1"></i>Start Processing
                    </button>
                  </div>

                  <div class="row g-2 mt-2">
                    <div class="col-md-3"><label class="hms-label">Result</label>
                      <select v-if="resultOptions(item).length" class="hms-input" v-model="forms[item.item_id].result_value">
                        <option value="">— Select result —</option>
                        <option v-for="opt in resultOptions(item)" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <input v-else class="hms-input" v-model="forms[item.item_id].result_value" placeholder="Value" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Unit</label>
                      <input class="hms-input" v-model="forms[item.item_id].result_unit" :placeholder="item.test_unit || ''" />
                    </div>
                    <div class="col-md-3"><label class="hms-label">Reference Range</label>
                      <input class="hms-input" v-model="forms[item.item_id].normal_range" :placeholder="defaultRange(item)" />
                    </div>
                    <div class="col-md-2"><label class="hms-label">Flag</label>
                      <select class="hms-input" v-model="forms[item.item_id].result_flag">
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="low">Low</option>
                        <option value="critical_high">Critical High</option>
                        <option value="critical_low">Critical Low</option>
                        <option value="abnormal">Abnormal</option>
                      </select>
                    </div>
                    <div class="col-md-2 d-flex align-items-end">
                      <button class="hms-btn hms-btn-primary w-100" @click="saveResult(item)" :disabled="processing || !forms[item.item_id].result_value">
                        <i class="mdi mdi-check me-1"></i>Save
                      </button>
                    </div>
                    <div class="col-12"><label class="hms-label">Notes (optional)</label>
                      <input class="hms-input" v-model="forms[item.item_id].result_notes" placeholder="Methodology, deviations, comments..." />
                    </div>
                  </div>

                  <div class="d-flex gap-2 mt-2">
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="useDefaultRange(item)" v-if="defaultRange(item)">
                      <i class="mdi mdi-auto-fix me-1"></i>Use default range
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-blue" @click="useDefaultUnit(item)" v-if="item.test_unit">
                      <i class="mdi mdi-auto-fix me-1"></i>Use default unit
                    </button>
                    <button class="hms-btn hms-btn-sm hms-btn-danger ms-auto" @click="cancelItem(item)">
                      <i class="mdi mdi-close-circle me-1"></i>Cancel
                    </button>
                  </div>
                </div>

                <!-- Resulted: show + verify option -->
                <div v-else-if="item.status === 'resulted' || item.status === 'verified'" class="hms-item-body">
                  <div class="hms-result-row">
                    <div>
                      <div class="hms-result-value" :class="flagColor(item.result_flag)">
                        {{ item.result_value }} <span class="hms-muted-sm">{{ item.result_unit }}</span>
                      </div>
                      <div class="hms-muted-sm">Range: {{ item.normal_range || '—' }}</div>
                      <div class="hms-muted-sm" v-if="item.result_notes">{{ item.result_notes }}</div>
                    </div>
                    <div class="ms-auto">
                      <span v-if="item.result_flag" class="hms-badge hms-badge-lg" :class="flagBadge(item.result_flag)">{{ item.result_flag.replace('_',' ') }}</span>
                    </div>
                  </div>
                  <div class="hms-muted-sm mt-2">
                    <i class="mdi mdi-account-check"></i> Result by {{ item.result_by_name || '—' }}
                    · {{ formatDateTime(item.result_entered_at) }}
                    <span v-if="item.verified_by_name"> · Verified by {{ item.verified_by_name }} · {{ formatDateTime(item.verified_at) }}</span>
                  </div>
                  <div class="d-flex gap-2 mt-2">
                    <button v-if="item.status === 'resulted'" class="hms-btn hms-btn-sm hms-btn-success" @click="verifyItem(item)" :disabled="processing">
                      <i class="mdi mdi-check-decagram me-1"></i>Verify
                    </button>
                    <button v-if="item.status === 'resulted'" class="hms-btn hms-btn-sm hms-btn-amber" @click="reEnterResult(item)">
                      <i class="mdi mdi-pencil me-1"></i>Edit
                    </button>
                  </div>
                </div>

                <!-- Cancelled -->
                <div v-else-if="item.status === 'cancelled'" class="hms-item-body">
                  <div class="hms-muted-sm"><i class="mdi mdi-close-circle"></i> Cancelled. {{ item.result_notes || '' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CANCEL MODAL -->
      <div class="hms-modal-overlay" v-if="cancelTarget" @click.self="cancelTarget=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-close-circle me-1"></i>Cancel Test</span>
            <button class="hms-modal-close" @click="cancelTarget=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3"><i class="mdi mdi-information-outline me-1"></i>Cancelling: <strong>{{ cancelTarget.test_name }}</strong></div>
          <label class="hms-label">Reason</label>
          <textarea class="hms-input mb-3" rows="3" v-model="cancelReason" placeholder="e.g. Sample haemolysed; patient withdrew consent..."></textarea>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="cancelTarget=null">Back</button>
            <button class="hms-btn hms-btn-danger flex-fill" @click="confirmCancel" :disabled="processing">Confirm Cancel</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-h { color: var(--hms-h); font-size: 16px; font-weight: 700; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; flex: 1; min-width: 120px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-warning { color: #ca8a04; }
.hms-stat-info    { color: var(--hms-info); }
.hms-stat-purple  { color: #9b59b6; }
.hms-stat-danger  { color: var(--hms-danger); }
.hms-stat-success { color: var(--hms-success); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 60px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }
.hms-empty-mini { text-align: center; padding: 16px; color: var(--hms-s); font-size: 13px; }
.hms-empty-mini i { font-size: 28px; color: var(--hms-success); display: block; margin-bottom: 6px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 180px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px 7px 34px; color: var(--hms-h); font-size: 13px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 7px 12px; color: var(--hms-h); font-size: 13px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 6px 10px; }
.hms-label { color: var(--hms-m); font-size: 11px; font-weight: 600; margin-bottom: 3px; display: block; }

.hms-q-list { display: flex; flex-direction: column; gap: 6px; max-height: 720px; overflow-y: auto; }
.hms-q-item { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-br); border-radius: 8px; padding: 10px 12px; cursor: pointer; transition: all .15s; }
.hms-q-item:hover { border-color: var(--hms-primary); }
.hms-q-item.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-q-item.pri-stat    { border-left-color: var(--hms-danger); }
.hms-q-item.pri-urgent  { border-left-color: var(--hms-warning); }
.hms-q-item.pri-routine { border-left-color: var(--hms-info); }
.hms-q-time { font-size: 11px; color: var(--hms-s); padding: 1px 8px; border: 1px solid var(--hms-br); border-radius: 50px; }
.hms-q-name { color: var(--hms-h); font-weight: 700; font-size: 13px; margin-top: 4px; }

.hms-progress-strip { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
.hms-pip-num { background: var(--hms-bg3); color: var(--hms-h); font-size: 11px; padding: 2px 8px; border-radius: 50px; }
.hms-pip-num.verified { background: rgba(40,167,69,.15); color: var(--hms-success); }

.hms-avatar-lg { width: 48px; height: 48px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 800; flex-shrink: 0; }

.hms-info-strip { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 6px; padding: 6px 10px; font-size: 12px; }
.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }

.hms-item-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid var(--hms-br); border-radius: 10px; padding: 14px; margin-bottom: 8px; }
.hms-item-card.st-resulted { border-left-color: var(--hms-success); }
.hms-item-card.st-verified { border-left-color: var(--hms-success); background: rgba(40,167,69,.04); }
.hms-item-card.st-cancelled { border-left-color: var(--hms-danger); opacity: 0.7; }
.hms-item-card.st-processing { border-left-color: #9b59b6; }
.hms-item-card.st-sample_collected { border-left-color: var(--hms-info); }
.hms-item-card.st-pending { border-left-color: var(--hms-warning); }
.hms-item-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
.hms-item-name { color: var(--hms-h); font-weight: 700; font-size: 14px; }
.hms-item-body { padding-top: 6px; }

.hms-result-row { display: flex; align-items: center; gap: 12px; }
.hms-result-value { font-size: 22px; font-weight: 800; color: var(--hms-h); }
.hms-result-value.hms-flag-h, .hms-result-value.hms-flag-critical_high { color: var(--hms-danger); }
.hms-result-value.hms-flag-l, .hms-result-value.hms-flag-critical_low  { color: var(--hms-warning); }
.hms-result-value.hms-flag-abnormal { color: #ca8a04; }
.hms-result-value.hms-flag-normal   { color: var(--hms-success); }
.hms-badge-lg { padding: 4px 12px; font-size: 12px; }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: var(--hms-success); color: #fff; }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }
.hms-btn-icon { background: none; border: none; color: var(--hms-s); font-size: 16px; cursor: pointer; padding: 0; }

.hms-ai-box { background: var(--hms-bg2); border: 1px solid var(--hms-primary-lt); border-left: 4px solid var(--hms-primary); border-radius: 8px; padding: 12px 16px; }
.hms-ai-body { color: var(--hms-h); font-size: 13px; line-height: 1.5; white-space: pre-wrap; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 460px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import SaveAndQueue from "@/components/hms-shared/SaveAndQueue.vue";
import UnpaidBanner from "@/components/hms-shared/UnpaidBanner.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "LabResultsEntry",
  components: { Navigation, SaveAndQueue, UnpaidBanner },
  data() {
    return {
      loading: true, processing: false, aiLoading: false,
      autoRefresh: true, refreshTimer: null,

      requests: [], stats: {},
      filterStatus: '', filterPriority: '', search: '',

      active: null,                // { Request, Items }
      forms: {},                   // keyed by item_id

      cancelTarget: null, cancelReason: '',

      aiResult: null,

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    filteredRequests() {
      var list = this.requests;
      if (this.filterPriority) list = list.filter(r => r.priority === this.filterPriority);
      var q = (this.search || '').trim().toLowerCase();
      if (q) {
        list = list.filter(r => ((r.request_number||'') + ' ' + (r.first_name||'') + ' ' + (r.last_name||'') + ' ' + (r.patient_no||'')).toLowerCase().includes(q));
      }
      return list;
    },
    canBulkCollect() {
      return this.active && this.active.Items.some(i => i.status === 'pending');
    },
    canAiInterpret() {
      return this.active && this.active.Items.some(i => i.result_value);
    },
    canPrint() {
      return this.active && this.active.Items.some(i => i.status === 'resulted' || i.status === 'verified');
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
    this.refreshTimer = setInterval(() => { if (this.autoRefresh) this.loadAll(true); }, 30000);
  },
  watch: {
    filterStatus()   { this.loadAll(); },
    filterPriority() { this.loadAll(); }
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.toastTimer)   clearTimeout(this.toastTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ''; }, 2400);
    },
    async finaliseLab() {
      var pid = this.active && this.active.Request && this.active.Request.patient_id;
      if (!pid) { this.showToast("No patient selected", true); return; }
      if (!this.$refs.sqLab) return;
      this.processing = true;
      try {
        var q = await this.$refs.sqLab.queueIfSelected(pid);
        if (q && q.ok) this.showToast("Queued to " + q.dept_name + " (ticket " + q.ticket_number + ")");
        else if (q && q.message) this.showToast(q.message, true);
        else this.showToast("Pick a destination from the dropdown first", true);
      } finally { this.processing = false; }
    },

    initials(r) { var f=(r.first_name||'').charAt(0); var l=(r.last_name||'').charAt(0); return ((f+l).toUpperCase())||'?'; },
    ageStr(dob) { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return '?'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    formatMins(m) { if (m == null) return ''; if (m < 60) return m + 'm'; var h = Math.floor(m / 60); return h + 'h' + (m % 60 ? ' ' + (m % 60) + 'm' : ''); },
    formatDateTime(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleString(); },
    priorityBadge(p) { return ({ stat:'hms-badge-danger', urgent:'hms-badge-warning', routine:'hms-badge-info' })[p] || 'hms-badge-neutral'; },
    itemStatusBadge(s) { return ({ pending:'hms-badge-warning', sample_collected:'hms-badge-info', processing:'hms-badge-purple', resulted:'hms-badge-success', verified:'hms-badge-success', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    flagBadge(f) {
      var v = (f || '').toLowerCase();
      if (v === 'high' || v === 'critical_high') return 'hms-badge-danger';
      if (v === 'low'  || v === 'critical_low')  return 'hms-badge-warning';
      if (v === 'abnormal') return 'hms-badge-warning';
      return 'hms-badge-success';
    },
    flagColor(f) { return 'hms-flag-' + (f || 'normal'); },
    resultOptions(item) {
      if (!item.result_options) return [];
      return String(item.result_options).split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    },
    defaultRange(item) {
      // Pick best of male/female/child based on patient if available; fallback to whichever exists
      if (!this.active || !this.active.Request) return item.normal_range_male || '';
      var g = (this.active.Request.gender || '').toLowerCase();
      var dob = this.active.Request.dob;
      var ageY = 999;
      if (dob) { var d = new Date(dob); if (!isNaN(d)) ageY = (Date.now() - d.getTime()) / (365.25 * 24 * 3600000); }
      if (ageY < 16 && item.normal_range_child) return item.normal_range_child;
      if (g === 'female' && item.normal_range_female) return item.normal_range_female;
      return item.normal_range_male || item.normal_range_female || item.normal_range_child || '';
    },

    async loadAll(silent) {
      if (!silent) this.loading = true;
      try {
        var payload = { function: "getLabWorklist" };
        if (this.filterStatus)   payload.status   = this.filterStatus;
        if (this.filterPriority) payload.priority = this.filterPriority;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.requests = (r.data.data && r.data.data.Requests) || [];
          this.stats    = (r.data.data && r.data.data.Stats)    || {};
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    async selectRequest(reqId) {
      this.processing = true; this.aiResult = null;
      try {
        var r = await executeJson({ function: "getLabRequestDetail", request_id: reqId }, hms);
        if (r.data && r.data.success) {
          this.active = r.data.data;
          var f = {};
          (this.active.Items || []).forEach(it => {
            f[it.item_id] = {
              result_value: it.result_value || '',
              result_unit:  it.result_unit  || it.test_unit || '',
              normal_range: it.normal_range || this.defaultRange(it) || '',
              result_flag:  it.result_flag  || 'normal',
              result_notes: it.result_notes || ''
            };
          });
          this.forms = f;
        } else { this.showToast(r.data.message || 'Failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    async refreshActive() {
      if (!this.active) return;
      await this.selectRequest(this.active.Request.request_id);
      await this.loadAll(true);
    },

    async collectItem(item) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "collectSample", item_id: item.item_id, request_id: this.active.Request.request_id }, hms);
        if (r.data && r.data.success) { this.showToast('Sample collected'); await this.refreshActive(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },
    async bulkCollect() {
      if (!confirm('Mark all pending samples as collected?')) return;
      this.processing = true;
      var items = this.active.Items.filter(i => i.status === 'pending');
      try {
        for (var i = 0; i < items.length; i++) {
          await executeJson({ function: "collectSample", item_id: items[i].item_id, request_id: this.active.Request.request_id }, hms);
        }
        this.showToast(items.length + ' sample(s) collected');
        await this.refreshActive();
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },
    async startProcessing(item) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "startProcessingLabItem", item_id: item.item_id }, hms);
        if (r.data && r.data.success) { this.showToast('Moved to processing'); await this.refreshActive(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    useDefaultRange(item) { this.forms[item.item_id].normal_range = this.defaultRange(item); },
    useDefaultUnit(item)  { this.forms[item.item_id].result_unit  = item.test_unit || ''; },

    async saveResult(item) {
      var f = this.forms[item.item_id];
      if (!f.result_value) return;
      this.processing = true;
      try {
        var r = await executeJson({
          function: "enterLabResult",
          item_id: item.item_id, request_id: this.active.Request.request_id,
          result_value: f.result_value, result_unit: f.result_unit,
          normal_range: f.normal_range, result_flag: f.result_flag, result_notes: f.result_notes
        }, hms);
        if (r.data && r.data.success) { this.showToast('Result saved'); await this.refreshActive(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },
    reEnterResult(item) {
      // Roll back status to processing locally so the form re-renders editable
      item.status = 'processing';
      this.$forceUpdate();
    },

    async verifyItem(item) {
      this.processing = true;
      try {
        var r = await executeJson({ function: "verifyLabResult", item_id: item.item_id }, hms);
        if (r.data && r.data.success) { this.showToast('Result verified'); await this.refreshActive(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    cancelItem(item) { this.cancelTarget = item; this.cancelReason = ''; },
    async confirmCancel() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelLabItem", item_id: this.cancelTarget.item_id, reason: this.cancelReason }, hms);
        if (r.data && r.data.success) { this.showToast('Test cancelled'); this.cancelTarget = null; await this.refreshActive(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    printLabReport() {
      if (!this.active) return;
      window.open(this.$router.resolve({ name: 'print', params: { type: 'lab', id: this.active.Request.request_id }, query: { auto: '1' } }).href, '_blank');
    },

    async aiInterpret() {
      this.aiLoading = true; this.aiResult = null;
      try {
        var resulted = this.active.Items.filter(i => i.result_value);
        var summary = "Patient: " + this.active.Request.patient_name + " (" + this.ageStr(this.active.Request.dob) + ", " + this.active.Request.gender + ")\n";
        if (this.active.Request.clinical_notes) summary += "Clinical: " + this.active.Request.clinical_notes + "\n\n";
        summary += "Results:\n" + resulted.map(i => "- " + i.test_name + ": " + i.result_value + " " + (i.result_unit||'') + (i.normal_range ? " (range " + i.normal_range + ")" : "") + (i.result_flag ? " [" + i.result_flag + "]" : "")).join("\n");

        var r = await executeJson({ function: "aiLabInterpretation", lab_results: summary }, hms);
        if (r.data && r.data.success) {
          this.aiResult = (r.data.data && (r.data.data.interpretation || r.data.data.response || r.data.data.raw)) || JSON.stringify(r.data.data);
        } else { this.showToast(r.data.message || 'AI failed', true); }
      } catch(e) { this.showToast('Connection error', true); }
      this.aiLoading = false;
    }
  }
};
</script>
